import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import BetaFeedbackButton from '@/components/BetaFeedbackButton';
import { track } from '@/lib/analytics';
import {
  X, ArrowRight, CheckCircle, Sparkle, ArrowLeft,
  House, Heart, Gauge, IdentificationCard, Coins, Path, ClockClockwise, Calendar
} from '@phosphor-icons/react';

const STEP_ICONS = {
  '/household': House,
  '/wealth-health': Heart,
  '/retirement-gap': Gauge,
  '/social-security': IdentificationCard,
  '/roth-conversion': Coins,
  '/scenario-planner': Path,
  '/timeline': ClockClockwise,
  'consultation': Calendar,
};

const OnboardingModal = ({ open, steps, onComplete, onDismiss }) => {
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setIndex(0);
      track('onboarding_started');
    }
  }, [open]);

  if (!open || !steps || steps.length === 0) return null;

  const isLast = index === steps.length - 1;
  const step = steps[index];
  const Icon = step.external ? STEP_ICONS.consultation : (STEP_ICONS[step.path] || Sparkle);

  const handleGoToStep = () => {
    if (step.external) {
      track('consultation_clicked', { source: 'onboarding_step' });
      window.open(step.path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(step.path);
    }
    onDismiss?.();
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto py-6 fade-in"
      data-testid="onboarding-modal"
    >
      <div className="bg-white rounded-md shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden my-auto">
        <div className="bg-[#0A1128] p-6 relative">
          <button
            onClick={onDismiss}
            data-testid="onboarding-dismiss-button"
            className="absolute top-4 right-4 text-white/70 hover:text-white"
            aria-label="Dismiss onboarding"
          >
            <X size={20} />
          </button>
          <p className="text-xs uppercase tracking-[0.15em] text-[#C5A059]">Welcome to PLANIVANCE AI</p>
          <h2 className="cabinet-grotesk text-2xl sm:text-3xl font-medium text-white mt-2">
            Your 8-step financial planning journey
          </h2>
          <p className="text-sm text-[#A0AABF] mt-2">
            PLANIVANCE AI is an educational planning tool. The Wealth Health Score&trade; is your financial assessment experience. None of this is personalized advice — book a consultation for that.
          </p>
        </div>

        <div className="p-8">
          <div className="flex items-center gap-2 mb-6">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-all ${
                  i === index ? 'bg-[#C5A059]' : i < index ? 'bg-[#0A1128]' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>

          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-full bg-[#C5A059]/15 text-[#C5A059] flex items-center justify-center flex-shrink-0">
              <Icon size={28} weight="duotone" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#C5A059]">
                Step {step.id} of {steps.length}
              </p>
              <h3 className="cabinet-grotesk text-2xl font-medium text-[#0A1128] mt-1">
                {step.title}
              </h3>
              <p className="text-base text-[#4A5568] mt-3 leading-relaxed">
                {step.description}
              </p>
              {!step.external && (
                <p className="text-xs text-[#A0AABF] mt-3 ibm-plex">
                  Menu path: Dashboard <ArrowRight size={10} className="inline" /> {step.title}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mt-8 gap-3 flex-wrap">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIndex(Math.max(0, index - 1))}
                disabled={index === 0}
                data-testid="onboarding-prev-button"
                className="border-slate-300"
              >
                <ArrowLeft size={16} className="mr-2" /> Back
              </Button>
              <Button
                onClick={handleGoToStep}
                data-testid="onboarding-goto-step-button"
                variant="outline"
                className="border-[#C5A059] text-[#0A1128]"
              >
                {step.external ? 'Book Now' : 'Open This Step'}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={onDismiss}
                data-testid="onboarding-skip-button"
                className="text-[#4A5568]"
              >
                Skip for now
              </Button>
              {!isLast ? (
                <Button
                  onClick={() => setIndex(index + 1)}
                  data-testid="onboarding-next-button"
                  className="bg-[#0A1128] hover:bg-[#152145]"
                >
                  Next <ArrowRight size={16} className="ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={onComplete}
                  data-testid="onboarding-complete-button"
                  className="bg-[#C5A059] hover:bg-[#B08F4A] text-[#0A1128]"
                >
                  <CheckCircle size={16} className="mr-2" weight="bold" /> Got it, start planning
                </Button>
              )}
            </div>
          </div>

          {isLast && (
            <div className="mt-6 pt-6 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs text-[#4A5568] leading-relaxed sm:flex-1 sm:min-w-0">
                <strong className="text-[#0A1128]">Help us shape PLANIVANCE AI.</strong> As you explore, share what's working and what isn't — it directly informs the roadmap.
              </p>
              <div className="w-full sm:w-auto sm:flex-shrink-0">
                <BetaFeedbackButton testid="onboarding-beta-feedback-button" label="Share Beta Feedback" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
